import {
    Box,
    Button,
    Flex,
    HStack,
    Icon,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { TbBuilding } from 'react-icons/tb';
import RefreshButton from '../../../components/RefreshButton';
import LocationSelector from '../../../components/LocationSelector';
import CollapsibleSearch, { LIST_PAGE_SEARCH_WIDTH } from '../../../components/CollapsibleSearch';
import TableSettingsButton from '../../../components/TableSettingsButton';
import InventoryAdvancedFilters from '../../../components/inventory/InventoryAdvancedFilters';
import { InventoryOptionsImportExport } from '../../../components/inventory/InventoryOptionsImportExport';
import { InventoryImportPreviewPanel } from '../../../components/inventory/InventoryImportPreviewPanel';
import {
    InventoryImportDefaultValueField,
    validateInventoryImportDefaults,
} from '../../../components/inventory/InventoryImportDefaultValueField';
import { ImportMappingModal } from '../../../components/import/ImportMappingModal';
import { ImportUploadModal } from '../../../components/import/ImportUploadModal';
import { downloadImportTemplate } from '../../../functions/import/downloadImportTemplate';
import { isSupportedImportFile } from '../../../functions/import/parseTabularFile';
import {
    INVENTORY_LIST_TAB_ICONS,
    INVENTORY_ACTIVITY_BUTTON_ICON,
} from './useInventoryListPage';
import { InventoryListTable } from './InventoryListTable';
import { InventoryListGrid } from './InventoryListGrid';
import { InventoryListModals } from './InventoryListModals';
import AllInventoryActivityLogDrawer from '../../../modals/inventory/AllInventoryActivityLogDrawer';
import type { InventoryListPageModel } from './useInventoryListPage';
import type { InventoryStatusFilter } from '../../../types/inventory';
import type { ImportTargetField } from '../../../functions/import/fuzzyColumnMatch';
import { InventoryPurchaseOrdersPanel } from '../purchaseOrders/InventoryPurchaseOrdersPanel';
import { usePendingPurchaseOrdersCount } from '../../../hooks/inventory/usePendingPurchaseOrdersCount';
import { PendingCountBadge } from '../../../components/PendingCountBadge';
import usePageTitle from '../../../hooks/usePageTitle';
import ListTabRow from '../../../components/ListTabRow';
import { InventoryTotalValueCard } from './InventoryTotalValueCard';

const TAB_LABELS: Record<InventoryStatusFilter, string> = {
    active: 'All',
    flagged: 'Flagged',
    discontinued: 'Discontinued',
};

export function InventoryListView({ page }: { page: InventoryListPageModel }) {
    const {
        statusFilter,
        searchQuery,
        setSearchQuery,
        purchaseOrdersSearchQuery,
        setPurchaseOrdersSearchQuery,
        additionalFilters,
        handleFiltersChange,
        tableSettings,
        updateTableSettings,
        colWidths,
        resetColWidths,
        handleRefresh,
        isRefetching,
        totalInventoryValue,
        isSummaryLoading,
        onAddDrawerOpen,
        isActivityLogOpen,
        onActivityLogOpen,
        onActivityLogClose,
        toolbarRef,
        locationCompact,
        searchCompact,
        locationIds,
        handleLocationChange,
        handleStatusFilterChange,
        handleImportPreviewTab,
        handlePurchaseOrdersTab,
        handleSelectImportJobId,
        handleRequestImport,
        handleImportFileSelected,
        handleImportUploadClose,
        handleImportMappingClose,
        handleImportMappingSubmit,
        buildFilteredExportInput,
        importTargetFields,
        listViewMode,
        selectedImportJobId,
        focusedPurchaseOrderId,
        importFile,
        isImportUploadOpen,
        isImportMappingOpen,
        hasPendingImportJob,
        openEditDrawer,
        optimisticImports,
        selectedOptimisticTempId,
        handleSelectOptimisticTempId,
        onImportHistoryOpen,
        onExportHistoryOpen,
        handleImportActivityClick,
        handleExportActivityClick,
    } = page;

    usePageTitle('Inventory');
    const viewMode = tableSettings.viewMode ?? 'grid';
    const showImportPreview = listViewMode === 'importPreview';
    const showPurchaseOrders = listViewMode === 'purchaseOrders';
    const showListChrome = !showImportPreview && !showPurchaseOrders;
    const { total: pendingPurchaseOrdersCount } = usePendingPurchaseOrdersCount(
        true,
        locationIds,
    );

    const [confirmedDefaultRefs, setConfirmedDefaultRefs] = useState<Set<string>>(
        () => new Set(),
    );

    const handleImportMappingCloseWithReset = () => {
        setConfirmedDefaultRefs(new Set());
        handleImportMappingClose();
    };

    const renderImportConstantField = (
        field: ImportTargetField,
        value: string,
        onChange: (next: string) => void,
        error?: string,
    ) => (
        <InventoryImportDefaultValueField
            field={field}
            value={value}
            onChange={onChange}
            error={error}
            onRefConfirmed={(fieldId, confirmed) => {
                setConfirmedDefaultRefs((prev) => {
                    const next = new Set(prev);
                    if (confirmed) next.add(fieldId);
                    else next.delete(fieldId);
                    return next;
                });
            }}
        />
    );

    const validateImportConstants = useCallback(
        (constants: Record<string, string>, fieldsNeedingConstant: ImportTargetField[]) =>
            validateInventoryImportDefaults(
                constants,
                fieldsNeedingConstant,
                confirmedDefaultRefs,
            ),
        [confirmedDefaultRefs],
    );

    return (
        <Box
            w="calc(100% + 2rem)"
            mx={-4}
            mt={-4}
            mb={-2}
            pb={0}
            h="calc(100dvh - var(--partbay-topbar))"
            display="flex"
            flexDirection="column"
            overflow="hidden"
        >
            {/* Row 1 — status tabs + toolbar actions, hairline underneath (ENG-2297). */}
            <ListTabRow
                actions={
                    <HStack spacing={0} flexShrink={0} align="center">
                        {showListChrome ? (
                            <>
                                <InventoryTotalValueCard
                                    value={totalInventoryValue}
                                    isLoading={isSummaryLoading}
                                />
                                <Box w="17px" flexShrink={0} aria-hidden />
                            </>
                        ) : null}
                        <HStack spacing={2}>
                            <Button
                                size="sm"
                                bg="#0071E3"
                                color="white"
                                _hover={{ bg: '#3544a0' }}
                                onClick={onAddDrawerOpen}
                            >
                                Add
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="gray"
                                color="gray.600"
                                leftIcon={<Icon as={INVENTORY_ACTIVITY_BUTTON_ICON} boxSize={3.5} />}
                                onClick={onActivityLogOpen}
                            >
                                Activity
                            </Button>
                            <TableSettingsButton
                                settings={tableSettings}
                                onUpdateSettings={updateTableSettings}
                                hasCustomColumnWidths={Object.keys(colWidths).length > 0}
                                onResetColumnWidths={resetColWidths}
                                showViewMode
                                panelTitle="Inventory Options"
                                footerSections={({ closePanel }) => (
                                    <InventoryOptionsImportExport
                                        buildFilteredExportInput={buildFilteredExportInput}
                                        onRequestImport={handleRequestImport}
                                        onRequestCloseDrawer={closePanel}
                                        onOpenImportHistory={onImportHistoryOpen}
                                        onOpenExportHistory={onExportHistoryOpen}
                                    />
                                )}
                            />
                            <RefreshButton refetch={handleRefresh} isLoading={isRefetching} />
                        </HStack>
                    </HStack>
                }
            >
                    {(['active', 'flagged', 'discontinued'] as const).map((filter) => {
                        const isActive = showListChrome && statusFilter === filter;
                        return (
                            <Button
                                key={filter}
                                size="sm"
                                h="32px"
                                px={4}
                                borderRadius="full"
                                fontWeight="medium"
                                fontSize="sm"
                                flexShrink={0}
                                bg={isActive ? '#0071E3' : 'transparent'}
                                color={isActive ? 'white' : 'gray.600'}
                                _hover={{ bg: isActive ? '#0071E3' : 'gray.100' }}
                                _active={{ bg: isActive ? '#0071E3' : 'gray.100' }}
                                onClick={() => handleStatusFilterChange(filter)}
                                leftIcon={
                                    <Icon as={INVENTORY_LIST_TAB_ICONS[filter]} boxSize={3.5} />
                                }
                            >
                                {TAB_LABELS[filter]}
                            </Button>
                        );
                    })}
                    <Box position="relative" overflow="visible" flexShrink={0} display="inline-flex" alignItems="center">
                        <Button
                            size="sm"
                            h="32px"
                            px={4}
                            borderRadius="full"
                            fontWeight="medium"
                            fontSize="sm"
                            bg={showPurchaseOrders ? '#0071E3' : 'transparent'}
                            color={showPurchaseOrders ? 'white' : 'gray.600'}
                            _hover={{ bg: showPurchaseOrders ? '#0071E3' : 'gray.100' }}
                            _active={{ bg: showPurchaseOrders ? '#0071E3' : 'gray.100' }}
                            onClick={handlePurchaseOrdersTab}
                            leftIcon={
                                <Icon as={INVENTORY_LIST_TAB_ICONS.purchaseOrders} boxSize={3.5} />
                            }
                        >
                            Purchase Orders
                        </Button>
                        <PendingCountBadge count={pendingPurchaseOrdersCount} />
                    </Box>
                    {hasPendingImportJob ? (
                        <Button
                            size="sm"
                            h="32px"
                            px={4}
                            borderRadius="full"
                            fontWeight="medium"
                            fontSize="sm"
                            flexShrink={0}
                            bg={showImportPreview ? '#0071E3' : 'transparent'}
                            color={showImportPreview ? 'white' : 'gray.600'}
                            _hover={{ bg: showImportPreview ? '#0071E3' : 'gray.100' }}
                            _active={{ bg: showImportPreview ? '#0071E3' : 'gray.100' }}
                            onClick={handleImportPreviewTab}
                            leftIcon={
                                <Icon
                                    as={INVENTORY_LIST_TAB_ICONS.importPreview}
                                    boxSize={3.5}
                                />
                            }
                        >
                            Import Preview
                        </Button>
                    ) : null}
            </ListTabRow>

            {/* Row 2 — search + filters left, location far right (ENG-2297 #4). */}
            {showListChrome || showPurchaseOrders ? (
                <Flex
                    ref={toolbarRef}
                    align="center"
                    my={3}
                    px={6}
                    flexShrink={0}
                    gap={2}
                >
                    {showListChrome ? (
                        <CollapsibleSearch
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by Mfg. Part # and Name"
                            isCompact={searchCompact}
                            width={LIST_PAGE_SEARCH_WIDTH}
                            truncatePlaceholder
                        />
                    ) : null}
                    {showPurchaseOrders ? (
                        <CollapsibleSearch
                            value={purchaseOrdersSearchQuery}
                            onChange={(e) => setPurchaseOrdersSearchQuery(e.target.value)}
                            placeholder="Search by RO #, Mfg. Part #, Location"
                            isCompact={searchCompact}
                            width={LIST_PAGE_SEARCH_WIDTH}
                            truncatePlaceholder
                        />
                    ) : null}
                    {showListChrome ? (
                        <InventoryAdvancedFilters
                            filters={additionalFilters}
                            onFiltersChange={handleFiltersChange}
                            locationIds={locationIds}
                        />
                    ) : null}
                    <Box ml="auto" flexShrink={0}>
                        <LocationSelector
                            storageKey="inventory_location_ids"
                            selectedLocations={locationIds}
                            onLocationChange={handleLocationChange}
                            width="260px"
                            icon={TbBuilding}
                            iconOnly={locationCompact}
                        />
                    </Box>
                </Flex>
            ) : null}

            {showPurchaseOrders ? (
                <Box flex={1} overflowY="auto" minH={0}>
                    <InventoryPurchaseOrdersPanel
                        locationIds={locationIds}
                        purchaseOrderId={focusedPurchaseOrderId}
                        searchQuery={purchaseOrdersSearchQuery}
                    />
                </Box>
            ) : showImportPreview ? (
                <InventoryImportPreviewPanel
                    selectedJobId={selectedImportJobId}
                    onSelectJobId={handleSelectImportJobId}
                    optimisticImports={optimisticImports}
                    selectedOptimisticTempId={selectedOptimisticTempId}
                    onSelectOptimisticTempId={handleSelectOptimisticTempId}
                />
            ) : viewMode === 'grid' ? (
                <InventoryListGrid page={page} />
            ) : (
                <InventoryListTable page={page} />
            )}
            <InventoryListModals page={page} />
            <AllInventoryActivityLogDrawer
                isOpen={isActivityLogOpen}
                onClose={onActivityLogClose}
                onItemClick={openEditDrawer}
                onImportActivityClick={handleImportActivityClick}
                onExportActivityClick={handleExportActivityClick}
            />
            <ImportUploadModal
                isOpen={isImportUploadOpen}
                onClose={handleImportUploadClose}
                onFileSelected={handleImportFileSelected}
                onDownloadTemplate={() => downloadImportTemplate(importTargetFields)}
                title="Upload File"
                supportedFormatsLabel="CSV, XLSX"
                maxSizeMB={25}
                isSupportedFile={isSupportedImportFile}
            />
            <ImportMappingModal
                isOpen={isImportMappingOpen}
                onClose={handleImportMappingCloseWithReset}
                file={importFile}
                targetFields={importTargetFields}
                renderConstantField={renderImportConstantField}
                validateConstants={validateImportConstants}
                onSubmit={handleImportMappingSubmit}
                submitLabel="Start Import"
            />
        </Box>
    );
}
